/** Who wrote a comment (RFC 0324 3a). */

import { projectSessions } from '../session/reads'
import type { CommentAuthor, PersonEntry } from './types'

/** Eight hues, far enough apart that neighbours are not confused. */
const TINTS = [
  '#3b82f6',
  '#22c55e',
  '#eab308',
  '#ef4444',
  '#a855f7',
  '#14b8a6',
  '#f97316',
  '#ec4899',
]

/** A colour picked from an id, so the same id is always the same colour. */
export function tintFor(id: string): string {
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return TINTS[(h >>> 0) % TINTS.length] as string
}

/** "Nassar Hayat" is NH, "priya" is P. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((w) => w[0] ?? '')
  return letters.join('').toUpperCase()
}

/**
 * The project's people, keyed by id. Strings are coerced because a value off
 * the log may be an Automerge string object rather than a JS string.
 */
export function projectPeople(projectDoc: unknown): PersonEntry[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const people = (projectDoc as { people?: unknown }).people
  if (!people || typeof people !== 'object') return []
  const out: PersonEntry[] = []
  for (const raw of Object.values(people as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue
    const p = raw as Record<string, unknown>
    const id = p.id == null ? '' : String(p.id)
    const name = p.name == null ? '' : String(p.name)
    if (!id || !name) continue
    out.push({
      id,
      name,
      ...(p.accountId ? { accountId: String(p.accountId) } : {}),
      ...(p.avatarUrl ? { avatarUrl: String(p.avatarUrl) } : {}),
      ...(p.tint ? { tint: String(p.tint) } : {}),
    })
  }
  return out
}

/**
 * Who wrote a comment, ready to draw.
 *
 * ONE FUNCTION, so the column, the margin, the review app and the bridge never
 * disagree about a name. An agent's comment resolves through `authorSessionId`
 * to its session's title, so "the agent" means a particular agent. A person is
 * found by their id, or by the account they later signed in as. Anything else,
 * including every comment written before people existed, shows the string that
 * was stored: guessing a name would be worse.
 */
export function projectAuthor(
  projectDoc: unknown,
  comment: { author: string; authorSessionId?: string },
): CommentAuthor {
  if (comment.authorSessionId) {
    const session = projectSessions(projectDoc).find(
      (s) => s.id === comment.authorSessionId,
    )
    const label = session?.title ?? comment.authorSessionId.slice(0, 8)
    return {
      label,
      kind: 'agent',
      initials: initialsOf(label),
      tint: tintFor(comment.authorSessionId),
      sessionId: comment.authorSessionId,
    }
  }
  const author = comment.author
  const person = projectPeople(projectDoc).find(
    (p) =>
      p.id === author || (p.accountId !== undefined && p.accountId === author),
  )
  if (person) {
    return {
      label: person.name,
      kind: 'person',
      initials: initialsOf(person.name),
      tint: person.tint ?? tintFor(person.id),
      ...(person.avatarUrl ? { avatarUrl: person.avatarUrl } : {}),
    }
  }
  return {
    label: author,
    kind: 'person',
    initials: initialsOf(author),
    tint: tintFor(author),
  }
}
